"use client";

import {
  Column,
  ColumnOrderState,
  Header,
  Table,
  flexRender,
} from "@tanstack/react-table";
import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";

import { cn } from "@/lib/utils";
import Fliter from "../Filter";
// import IndeterminateCheckbox from "../IndeterminateCheckbox";
import Sort from "../Sort";
import { Button } from "@/components/ui/button";
import useLocaleRouter from "@/hooks/useLocaleRouter";
import { useScopedI18n } from "@/messages/client";
import HeaderCell from "./Cell";

const reorderColumn = (
  draggedColumnId: string,
  targetColumnId: string,
  columnOrder: string[]
): ColumnOrderState => {
  columnOrder.splice(
    columnOrder.indexOf(targetColumnId),
    0,
    columnOrder.splice(columnOrder.indexOf(draggedColumnId), 1)[0] as string
  );
  return [...columnOrder];
};

export default function TableHeader<T extends object>({
  header,
  table,
  categorySlug,
}: {
  header: Header<T, unknown>;
  table: Table<T>;
  categorySlug?: string;
}) {
  const t = useScopedI18n("选型器");

  const { getState, setColumnOrder } = table;
  const { columnOrder } = getState();
  const { column } = header;

  const ref = useRef<HTMLTableCellElement>(null);

  const [, drop] = useDrop({
    accept: "column",
    hover(item, monitor) {
      if (!ref.current) return;

      const dragIndex = columnOrder.indexOf(item.id);
      const hoverIndex = columnOrder.indexOf(column.id);

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset?.y ?? 0) - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      const newColumnOrder = reorderColumn(item.id, column.id, columnOrder);

      setColumnOrder(newColumnOrder);
    },
    drop: (draggedColumn: Column<T>) => {
      const newColumnOrder = reorderColumn(
        draggedColumn.id,
        column.id,
        columnOrder
      );

      setColumnOrder(newColumnOrder);
    },
  });

  const [{ isDragging }, drag] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    item: () => column,
    type: "column",
  });

  drag(drop(ref));

  return (
    <div
      className={cn(
        "transition-opacity duration-300",
        isDragging && "opacity-50",
        {
          "product_filter_sticky bg-[#f8f8f8]":
            header.column.id === "material_number",
        }
      )}
      style={{
        width: header.getSize(),
      }}>
      <div
        className={cn(
          "relative w-full overflow-hidden border-b border-[#ccc] transition-colors duration-300 hover:bg-[#e8e8e8]"
        )}>
        <div
          ref={ref}
          className={cn(
            "flex min-h-[5rem] flex-wrap overflow-hidden whitespace-break-spaces px-2 py-3 text-sm"
          )}>
          {header.isPlaceholder ? null : (
            <div
              className="flex flex-1 space-x-1 break-all"
              onClick={header.column.getToggleSortingHandler()}>
              {header.column.id === "material_number" ? (
                flexRender(header.column.columnDef.header, header.getContext())
              ) : (
                <HeaderCell header={header} />
              )}

              {header.column.getCanSort() && (
                <Sort column={header.column} table={table} />
              )}
            </div>
          )}
        </div>
        {header.column.id !== "material_number" && (
          <div
            className="absolute right-0 top-0 flex h-full w-2 cursor-ew-resize touch-none select-none items-center after:absolute after:right-0 after:h-3/4 after:w-px after:border-l after:border-[#ccc]"
            onMouseDown={header.getResizeHandler()}
            onTouchStart={header.getResizeHandler()}></div>
        )}

        {column.getIsFiltered() && (
          <div className="absolute -top-[7px] right-[7px] z-30 h-0 w-0 rotate-90 border-b-[14px] border-r-[14px] border-t-[14px] border-transparent border-t-primary"></div>
        )}
      </div>

      <div
        className={cn(
          "flex h-10 items-center justify-between px-3 font-normal transition-colors duration-300 hover:bg-[#e8e8e8]"
        )}>
        {!["images"].includes(column.id) && header.column.getCanFilter() && (
          <Fliter column={header.column} table={table} />
        )}
      </div>
    </div>
  );
}
